import { useState, useEffect } from 'react'
import { useGameStore } from '../stores/gameStore'

export default function Lobby() {
  const {
    nickname,
    selectedRole,
    availableRooms,
    error,
    createRoom,
    joinRoom,
    refreshRooms,
    setScreen,
  } = useGameStore()

  const [roomName, setRoomName] = useState(`Gra ${nickname}`)
  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)

  useEffect(() => {
    refreshRooms()
    const interval = setInterval(refreshRooms, 3000)
    return () => clearInterval(interval)
  }, [refreshRooms])

  const handleCreate = async () => {
    if (!roomName.trim()) return
    setLoading(true)
    try {
      await createRoom(roomName)
    } finally {
      setLoading(false)
    }
  }

  const handleJoin = async (roomId: string) => {
    setLoading(true)
    try {
      await joinRoom(roomId)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-b from-emerald-dark to-gray-900 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-emerald-neon">Dostepne pokoje</h1>
          <p className="text-gray-400 text-sm">Dolacz do gry lub stworz nowa</p>
        </div>
        <button
          onClick={() => setScreen('menu')}
          className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
        >
          Powrot
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {/* Create room panel */}
      {showCreate ? (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-bold text-white mb-3">Stworz nowy pokoj</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="Nazwa pokoju..."
              className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white
                         focus:border-emerald-neon focus:outline-none"
              maxLength={30}
            />
            <button
              onClick={handleCreate}
              disabled={loading}
              className="px-6 py-2 bg-emerald-neon text-gray-900 font-bold rounded-lg
                         hover:bg-emerald-400 transition-colors disabled:opacity-50"
            >
              Stworz
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Anuluj
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowCreate(true)}
          className="w-full py-3 mb-6 bg-gray-800 border border-dashed border-gray-600 rounded-lg
                     text-gray-400 hover:text-emerald-neon hover:border-emerald-neon transition-colors"
        >
          + Stworz nowy pokoj
        </button>
      )}

      {/* Room list */}
      <div className="flex-1 overflow-auto space-y-3">
        {availableRooms.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <p className="text-lg mb-2">Brak dostepnych pokoi</p>
            <p className="text-sm">Stworz nowy pokoj lub poczekaj</p>
          </div>
        ) : (
          availableRooms.map((room) => (
            <div
              key={room.roomId}
              className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between
                         hover:border-gray-500 transition-colors"
            >
              <div>
                <h3 className="font-bold text-white">{room.name}</h3>
                <p className="text-sm text-gray-400">
                  {room.players}/{room.maxPlayers} graczy
                </p>
              </div>
              <button
                onClick={() => handleJoin(room.roomId)}
                disabled={loading || room.players >= room.maxPlayers}
                className="px-6 py-2 bg-emerald-neon text-gray-900 font-bold rounded-lg
                           hover:bg-emerald-400 transition-colors disabled:opacity-50"
              >
                Dolacz
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer info */}
      <div className="mt-4 text-center text-gray-500 text-sm">
        <span className="inline-block px-2 py-1 rounded" style={{ color: getRoleColor(selectedRole) }}>
          {selectedRole.toUpperCase()}
        </span>
        {' jako '}{nickname}
      </div>
    </div>
  )
}

function getRoleColor(role: string): string {
  const colors: Record<string, string> = {
    flux: '#00FF66',
    logic: '#4488FF',
    spark: '#FF8844',
    echo: '#AA44FF',
  }
  return colors[role] || '#ffffff'
}
